import { useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IoWarning } from 'react-icons/io5';

import { useAgreementsInfo } from '~/features/leadDetails/hooks/useAgreementsInfo';

export const DataProtectionAgreementWarning = ({
    hasSecondApplicant,
    leadId,
}: {
    leadId: string;
    hasSecondApplicant: boolean;
}) => {
    const { t } = useTranslation();
    const { agreementsInfo } = useAgreementsInfo(leadId);
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ left: 0, top: 0 });
    const triggerRef = useRef<HTMLDivElement>(null);

    const secondApplicantHasNotAgreed = hasSecondApplicant && !agreementsInfo.applicant2?.dataProtectionAgreed;
    const secondApplicantEmailVerificationDaysLeft = agreementsInfo.applicant2?.emailVerificationDaysLeft ?? null;

    useEffect(() => {
        if (showTooltip && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setTooltipPosition({
                left: rect.left,
                top: rect.bottom + 5,
            });
        }
    }, [showTooltip]);

    if (secondApplicantHasNotAgreed) {
        return (
            <>
                <div
                    className="relative z-20"
                    ref={triggerRef}
                    onMouseLeave={() => setShowTooltip(false)}
                    onMouseEnter={() => setShowTooltip(true)}
                >
                    <IoWarning size={16} color="orange" data-testid="warning-icon" />
                </div>
                {showTooltip &&
                    createPortal(
                        <div
                            className="fixed z-[9999] px-3 py-1 rounded bg-grey text-white text-sm whitespace-nowrap shadow-lg border"
                            style={{
                                left: tooltipPosition.left,
                                minWidth: '350px',
                                top: tooltipPosition.top,
                            }}
                        >
                            <div>{t('LEAD_DETAILS_GENERAL.DATA_PROTECTION_AGREEMENT_WARNING')}</div>
                            {secondApplicantEmailVerificationDaysLeft !== null && (
                                <div>
                                    {secondApplicantEmailVerificationDaysLeft <= 0
                                        ? t('LEAD_DETAILS_GENERAL.DATA_PROTECTION_AGREEMENT_DELETION_PENDING')
                                        : t('LEAD_DETAILS_GENERAL.DATA_PROTECTION_AGREEMENT_DAYS_LEFT', {
                                              count: secondApplicantEmailVerificationDaysLeft,
                                          })}
                                </div>
                            )}
                        </div>,
                        document.body,
                    )}
            </>
        );
    }

    return null;
};
